import { onDocumentReady, prefersReducedMotion } from '@theme/utilities';

const MOTION_GROUP_SELECTOR = '[data-motion-group]';
const MEDIA_READY_TIMEOUT = 2500;

class MotionController {
  /** @type {WeakMap<HTMLElement, IntersectionObserver>} */
  #observers = new WeakMap();

  init(root = document) {
    const groups = root.querySelectorAll(MOTION_GROUP_SELECTOR);
    groups.forEach((group) => this.#register(group));
  }

  #register(group) {
    if (!(group instanceof HTMLElement)) return;
    if (group.dataset.motionRegistered === 'true') return;

    group.dataset.motionRegistered = 'true';

    if (prefersReducedMotion() || !('IntersectionObserver' in window)) {
      this.#activate(group);
      return;
    }

    const threshold = this.#getThreshold(group);
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          if (entry.intersectionRatio < threshold) continue;

          this.#activateWhenReady(entry.target);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold,
        rootMargin: '0px 0px -5% 0px',
      }
    );

    this.#observers.set(group, observer);
    observer.observe(group);
  }

  #activate(group) {
    if (!(group instanceof HTMLElement)) return;
    if (group.dataset.motionPlayed === 'true') return;

    group.dataset.motionEnabled = 'true';
    delete group.dataset.motionPending;
    group.dataset.motionReady = 'true';
    group.dataset.motionPlayed = 'true';
  }

  async #activateWhenReady(group) {
    if (!(group instanceof HTMLElement)) return;
    if (group.dataset.motionPlayed === 'true') return;

    if (group.dataset.motionTrigger === 'media-ready') {
      await this.#waitForGroupMedia(group);
    }

    this.#playGroupAnimations(group);
    this.#activate(group);
  }

  #getThreshold(group) {
    const value = Number.parseFloat(group.dataset.motionThreshold || '');
    if (Number.isNaN(value)) return 0.35;
    return Math.min(Math.max(value, 0), 1);
  }

  async #waitForGroupMedia(group) {
    const mediaElements = this.#getPrimaryMediaElements(group);
    if (!mediaElements.length) return;

    await Promise.race([
      Promise.all(mediaElements.map((element) => this.#waitForMediaElement(element))),
      new Promise((resolve) => setTimeout(resolve, MEDIA_READY_TIMEOUT)),
    ]);
  }

  #getPrimaryMediaElements(group) {
    if (!(group instanceof HTMLElement)) return [];

    const currentSlide =
      group.querySelector("slideshow-slide[aria-hidden='false']") || group.querySelector('slideshow-slide:first-of-type');
    const mediaRoot = currentSlide?.querySelector("[data-motion-role='media']");
    if (!(mediaRoot instanceof HTMLElement)) return [];

    return [...mediaRoot.querySelectorAll('img, video')].filter((element) => this.#isVisibleMedia(element));
  }

  #isVisibleMedia(element) {
    if (!(element instanceof HTMLElement)) return false;

    const styles = window.getComputedStyle(element);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  }

  async #waitForMediaElement(element) {
    if (element instanceof HTMLImageElement) {
      if (element.complete && element.naturalWidth > 0) {
        try {
          await element.decode?.();
        } catch {}

        return;
      }

      await new Promise((resolve) => {
        const cleanup = () => {
          element.removeEventListener('load', handleDone);
          element.removeEventListener('error', handleDone);
        };

        const handleDone = () => {
          cleanup();
          resolve(null);
        };

        element.addEventListener('load', handleDone, { once: true });
        element.addEventListener('error', handleDone, { once: true });
      });

      return;
    }

    if (element instanceof HTMLVideoElement) {
      if (element.readyState >= 2 || element.poster) return;

      await new Promise((resolve) => {
        const cleanup = () => {
          element.removeEventListener('loadeddata', handleDone);
          element.removeEventListener('error', handleDone);
        };

        const handleDone = () => {
          cleanup();
          resolve(null);
        };

        element.addEventListener('loadeddata', handleDone, { once: true });
        element.addEventListener('error', handleDone, { once: true });
      });
    }
  }

  #playGroupAnimations(group) {
    const animatedElements = [
      ...(group.matches('[data-motion="fade-in"]') ? [group] : []),
      ...group.querySelectorAll('[data-motion="fade-in"]'),
    ];
    animatedElements.forEach((element) => this.#playFadeIn(element));
  }

  #playFadeIn(element) {
    if (!(element instanceof HTMLElement)) return;

    const styles = window.getComputedStyle(element);
    const durationMs = this.#parseTime(styles.getPropertyValue('--motion-duration'), 380);
    const delayMs = this.#parseTime(styles.getPropertyValue('--motion-delay'), 0);
    const easing = styles.getPropertyValue('--motion-easing').trim() || 'ease';
    const distance = styles.getPropertyValue('--motion-distance').trim() || '0px';
    const scaleFrom = styles.getPropertyValue('--motion-scale-from').trim() || '0.985';
    const isMedia = element.dataset.motionRole === 'media';
    const initialTransform = isMedia ? `scale(${scaleFrom})` : `translate3d(0, ${distance}, 0)`;

    element.style.willChange = 'opacity, transform';
    element.style.transition = 'none';
    element.style.opacity = '0';
    element.style.transform = initialTransform;

    void element.offsetWidth;

    element.style.transition = `opacity ${durationMs}ms ${easing} ${delayMs}ms, transform ${durationMs}ms ${easing} ${delayMs}ms`;

    requestAnimationFrame(() => {
      element.style.opacity = '1';
      element.style.transform = 'none';
    });

    window.setTimeout(() => {
      element.style.removeProperty('transition');
      element.style.removeProperty('opacity');
      element.style.removeProperty('transform');
      element.style.removeProperty('will-change');
    }, durationMs + delayMs + 80);
  }

  #parseTime(value, fallbackMs) {
    const normalized = value.trim();
    if (!normalized) return fallbackMs;
    if (normalized.endsWith('ms')) return parseFloat(normalized);
    if (normalized.endsWith('s')) return parseFloat(normalized) * 1000;

    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? fallbackMs : parsed;
  }
}

const motionController = new MotionController();

onDocumentReady(() => {
  motionController.init();
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  motionController.init(target);
});

document.addEventListener('shopify:section:select', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  motionController.init(target);
});

document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;

  const group = target.matches(MOTION_GROUP_SELECTOR) ? target : target.closest(MOTION_GROUP_SELECTOR);
  if (!(group instanceof HTMLElement)) return;
  if (group.dataset.motionPlayed === 'true') return;

  motionController.init(group.parentElement || document);
});
